import { useCallback, useEffect, useState } from "react";

import {
  sendHideChannel,
  sendMuteChannel,
  sendRemoveMembers,
  sendUnmuteChannel,
} from "./actions";
import { createRoomsFilterByUser } from "./utils";
import { getClient } from "../client";
import { DM_TYPE, channelOptsEverything } from "../constants";
import { flattenRawChannel, registerRawChannel } from "../entities/channels";
import { channelsByChannelId, connectedAsUser } from "../state";

async function fetchChannelById(channelId, loggedinUser) {
  const filter = {
    $or: [
      {
        type: DM_TYPE,
        members: { $in: [loggedinUser.id] },
      },
      createRoomsFilterByUser(loggedinUser),
    ],
  };
  filter.id = channelId;

  const rawChannels = await getClient().queryChannels(
    filter,
    {},
    channelOptsEverything
  );

  if (rawChannels && rawChannels.length > 0) {
    const rawChannel = rawChannels[0];
    registerRawChannel(rawChannel, channelOptsEverything, false);
    return flattenRawChannel(rawChannel);
  }

  throw new Error("Unable to find requested message or room");
}

// load information about a specific channel and preform actions on that channel
export default function useChannel({ channelId }) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = connectedAsUser.use();
  const { id: userId } = user;
  const channel = channelsByChannelId.use((obj) => obj[channelId]);

  useEffect(() => {
    (async function fetchChannel() {
      if (!channel) {
        try {
          setLoading(true);
          await fetchChannelById(channelId, user);
        } catch (err) {
          setError(err);
        }
      }
      setLoading(false);
    })();
  }, [channel, channelId, setLoading, user]);

  const leaveRoom = useCallback(
    () => sendRemoveMembers(channelId, userId).catch(setError),
    [channelId, userId]
  );

  const hideDM = useCallback(
    () => sendHideChannel(channelId).catch(setError),
    [channelId]
  );

  const mute = useCallback(
    (muteOptions = {}) =>
      sendMuteChannel(channelId, muteOptions).catch(setError),
    [channelId]
  );

  const unmute = useCallback(
    () => sendUnmuteChannel(channelId).catch(setError),
    [channelId]
  );

  return {
    channel,
    error,
    hideDM,
    leaveRoom,
    loading,
    mute,
    unmute,
  };
}
